import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getAllClientsReviewFiles } from "src/clients/FeatureService";
import {
  getAllPublishData,
  publishCommitApprove,
  publishCommitReject,
} from "src/clients/PublishService";
import publishstyles from "src/components/publish/Publish.module.css";
import { getClientDetails } from "src/redux/slices/clientDetailsSlice";
import { login, selectUser } from "src/redux/slices/userSlice";
import { Feature } from "src/types/Feature";
import { PublishDataDto } from "src/types/PublishDataDto";
import { PublishRequestStatus } from "src/types/PublishRequestStatus";
import { TOAST_MESSAGE } from "../constants/Constants";
import ViewFeature from "../features/view/ViewFeature";
import WarningPopup from "../modal/WarningPopup";
import NotifyError from "../notifications/NotifyError";
import NotifySuccess from "../notifications/NotifySuccess";
import reviewStylings from "../review/Review.module.css";
import Pagination from "../templates/Pagination";

const Review = () => {
  const [selectedUser, setSelectedUser] = useState<string>("ALL");
  const [publishedUserDetails, setPublishedUserDetails] = useState<
    PublishDataDto[]
  >([]);
  const [selectedUserDetails, setSelectedUserDetails] = useState<
    PublishDataDto[]
  >([]);
  const [checkedList, setCheckedList] = useState<PublishDataDto[]>([]);
  const [userNameList, setUserNameList] = useState<Set<string>>(
    new Set(["ALL"])
  );
  const [reviewFeatures, setReviewFeatures] = useState<Feature[] | null>(null);
  const [featureData, setFeatureData] = useState<Feature | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [commentPopup, setCommentPopup] = useState<boolean>(false);
  const [notFoundPopup, setNotFoundPopup] = useState<boolean>(false);
  const [reviewComment, setReviewComment] = useState<string>("");
  const [reviewStatus, setReviewStatus] = useState<string>("");
  const [isDisable, isLoading] = useState<boolean>(false);
  const [publishDataDto, setPublishDataDto] = useState<PublishDataDto | null>(
    null
  );
  const user = useSelector(selectUser);
  const clients = useSelector(getClientDetails);
  const dispatch = useDispatch();
  const fetchPublishData = async () => {
    setReviewFeatures(null);
    setUserNameList(new Set(["ALL"]));
    setCheckedList([]);
    setSelectedUserDetails([]);
    getAllPublishData(
      user.productCode,
      user.selectedClient === null
        ? user.clientId
        : clients.find(
            (client: any) => client.clientCode === user.selectedClient
          ).clientId,
      user.auth
    ).then(async (res) => {
      const response = await res.json();
      const resData = response.filter(
        (publishedData: any) =>
          publishedData.featureStatus === PublishRequestStatus.IN_PROGRESS
      );
      setPublishedUserDetails(resData);
      if (selectedUser === "ALL") {
        setSelectedUserDetails(resData);
      } else {
        let count = 0;
        resData.forEach((userDetails: PublishDataDto) => {
          if (userDetails.createdBy === selectedUser) {
            setSelectedUserDetails((prev) => [...prev, userDetails]);
            count += 1;
          }
        });
        if (count === 0) {
          setSelectedUser("ALL");
          setSelectedUserDetails(resData);
        }
      }
      const createdBySet = new Set<string>(
        resData.map((user: PublishDataDto) => user.createdBy)
      );
      setUserNameList(
        (prev) => new Set([...Array.from(prev), ...Array.from(createdBySet)])
      );
      setCurrentPage(resData.length > 0 ? 1 : 0);
      getAllClientsReviewFiles(
        user.auth,
        user.productCode,
        user.selectedClient
      ).then(async (res) => {
        if (res.ok) {
          const reviewData = await res.json();
          setReviewFeatures(reviewData);
        } else if (res.status === 408) {
          setReviewFeatures([]);
          toast.error(
            <NotifyError header={"Error"}>
              <Typography variant="body2">
                {TOAST_MESSAGE.FAILED_TO_CONNECT}
              </Typography>
            </NotifyError>,
            {
              icon: false,
              closeButton: false,
            }
          );
        } else {
          setReviewFeatures([]);
          toast.error(
            <NotifyError header={"Error"}>
              <Typography variant="body2">
                {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
              </Typography>
            </NotifyError>,
            {
              icon: false,
              closeButton: false,
            }
          );
        }
      });
    });
    dispatch(
      login({
        userDetailsId: user.userDetailsId,
        userId: user.userId,
        password: user.password,
        email: user.email,
        role: user.role,
        permissionsList: user.permissionsList,
        productCode: user.productCode,
        firstName: user.firstName,
        lastName: user.lastName,
        products: user.products,
        filesStatus: false,
        clientId: user.clientId,
        clientCode: user.clientCode,
        selectedClient: user.selectedClient,
        auth: user.auth,
      })
    );
  };
  useEffect(() => {
    fetchPublishData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.productCode, user.selectedClient]);

  const handleUserSelect = (event: any) => {
    let { value } = event.target;
    setSelectedUser(value);
    setCurrentPage(1);
    if (value === "ALL") {
      setSelectedUserDetails(publishedUserDetails);
    } else {
      setSelectedUserDetails([]);
      publishedUserDetails.forEach((userDetails: PublishDataDto) => {
        if (userDetails.createdBy === value) {
          setSelectedUserDetails((prev) => [...prev, userDetails]);
        }
      });
    }
  };

  const handleSelect = (userIndex: number) => {
    const index = checkedList.indexOf(currentPageItems[userIndex]);
    if (index !== -1) {
      const updatedFeatures = [...checkedList];
      updatedFeatures.splice(index, 1);
      setCheckedList(updatedFeatures);
    } else {
      setCheckedList([...checkedList, currentPageItems[userIndex]]);
    }
  };

  const handleSelectAll = () => {
    const currentList: PublishDataDto[] = currentPageItems;
    if (currentPageItems.every((item) => checkedList.includes(item))) {
      // For deSelecting All
      const newSelectedList = checkedList.filter(
        (item) => !currentPageItems.includes(item)
      );
      setCheckedList(newSelectedList);
    } else {
      // For Selecting All
      const newSelectedList = currentList.filter(
        (featureName) => !checkedList.includes(featureName)
      );
      setCheckedList([...checkedList, ...newSelectedList]);
    }
  };

  const handleItemsPerPageChange = (event: any) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleRequestReject = (publishDataDto?: PublishDataDto) => {
    setCommentPopup(true);
    setReviewStatus("reject");
    if (publishDataDto) setPublishDataDto(publishDataDto);
  };

  const handleRequestApprove = (publishDataDto?: PublishDataDto) => {
    setCommentPopup(true);
    setReviewStatus("approve");
    if (publishDataDto) setPublishDataDto(publishDataDto);
  };

  const handleCommentOk = () => {
    isLoading(true);
    if (reviewStatus === "approve") {
      if (checkedList.length === 0 && publishDataDto) {
        publishCommitApprove(
          [publishDataDto],
          reviewFeatures ?? [],
          reviewComment,
          user.productCode,
          user.auth,
          user.selectedClient ?? user.clientCode
        ).then(async (res) => {
          if (res.ok) {
            fetchPublishData();
            setCheckedList([]);
            isLoading(false);
            const response = await res.json();
            if (response.length === 0) {
              toast.success(
                <NotifySuccess header={"Success"}>
                  <Typography variant="body2">
                    File Approved Successfully
                  </Typography>
                </NotifySuccess>,
                {
                  icon: false,
                  closeButton: false,
                }
              );
            } else {
              let errormessage = "";
              response.forEach((PRID: number) => {
                errormessage = errormessage + PRID + ", ";
              });
              toast.error(
                <NotifyError header={"Error"}>
                  <Typography variant="body2">
                    Failed to Approve PRId -
                    {errormessage.substring(0, errormessage.length - 2)}
                  </Typography>
                </NotifyError>,
                {
                  icon: false,
                  closeButton: false,
                }
              );
            }
          } else if (res.status === 408) {
            isLoading(false);
            setCheckedList([]);
            toast.error(
              <NotifyError header={"Error"}>
                <Typography variant="body2">
                  {TOAST_MESSAGE.FAILED_TO_CONNECT}
                </Typography>
              </NotifyError>,
              {
                icon: false,
                closeButton: false,
              }
            );
          } else {
            setCheckedList([]);
            isLoading(false);
            toast.error(
              <NotifyError header={"Error"}>
                <Typography variant="body2">
                  {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
                </Typography>
              </NotifyError>,
              {
                icon: false,
                closeButton: false,
              }
            );
          }
        });
      } else {
        publishCommitApprove(
          checkedList,
          reviewFeatures ?? [],
          reviewComment,
          user.productCode,
          user.auth,
          user.selectedClient ?? user.clientCode
        ).then(async (res) => {
          if (res.ok) {
            fetchPublishData();
            setCheckedList([]);
            isLoading(false);
            const response = await res.json();
            if (response.length === 0) {
              toast.success(
                <NotifySuccess header={"Success"}>
                  <Typography variant="body2">
                    Files Approved Successfully
                  </Typography>
                </NotifySuccess>,
                {
                  icon: false,
                  closeButton: false,
                }
              );
            } else {
              let errormessage = "";
              response.forEach((PRID: number) => {
                errormessage = errormessage + PRID + ", ";
              });
              toast.error(
                <NotifyError header={"Error"}>
                  <Typography variant="body2">
                    Failed to Approve PRId's -
                    {errormessage.substring(0, errormessage.length - 2)}
                  </Typography>
                </NotifyError>,
                {
                  icon: false,
                  closeButton: false,
                }
              );
            }
          } else if (res.status === 408) {
            isLoading(false);
            setCheckedList([]);
            toast.error(
              <NotifyError header={"Error"}>
                <Typography variant="body2">
                  {TOAST_MESSAGE.FAILED_TO_CONNECT}
                </Typography>
              </NotifyError>,
              {
                icon: false,
                closeButton: false,
              }
            );
          } else {
            setCheckedList([]);
            isLoading(false);
            toast.error(
              <NotifyError header={"Error"}>
                <Typography variant="body2">
                  {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
                </Typography>
              </NotifyError>,
              {
                icon: false,
                closeButton: false,
              }
            );
          }
        });
      }
    } else if (reviewStatus === "reject") {
      if (checkedList.length === 0 && publishDataDto) {
        publishCommitReject(
          [publishDataDto],
          reviewComment,
          user.productCode,
          user.auth,
          user.selectedClient
        ).then(async (res) => {
          if (res.ok) {
            fetchPublishData();
            setCheckedList([]);
            isLoading(false);
            const response = await res.json();
            if (response.length === 0) {
              toast.success(
                <NotifySuccess header={"Success"}>
                  <Typography variant="body2">
                    File Rejected Successfully
                  </Typography>
                </NotifySuccess>,
                {
                  icon: false,
                  closeButton: false,
                }
              );
            } else {
              let errormessage = "";
              response.forEach((RequestId: number) => {
                errormessage = errormessage + RequestId + ", ";
              });
              toast.error(
                <NotifyError header={"Error"}>
                  <Typography variant="body2">
                    Failed to Reject RequestId -
                    {errormessage.substring(0, errormessage.length - 2)}
                  </Typography>
                </NotifyError>,
                {
                  icon: false,
                  closeButton: false,
                }
              );
            }
          } else if (res.status === 408) {
            toast.error(
              <NotifyError header={"Error"}>
                <Typography variant="body2">
                  {TOAST_MESSAGE.FAILED_TO_CONNECT}
                </Typography>
              </NotifyError>,
              {
                icon: false,
                closeButton: false,
              }
            );
            setCheckedList([]);
            isLoading(false);
          } else {
            toast.error(
              <NotifyError header={"Error"}>
                <Typography variant="body2">
                  {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
                </Typography>
              </NotifyError>,
              {
                icon: false,
                closeButton: false,
              }
            );
            setCheckedList([]);
            isLoading(false);
          }
        });
      } else {
        publishCommitReject(
          checkedList,
          reviewComment,
          user.productCode,
          user.auth,
          user.selectedClient
        ).then(async (res) => {
          if (res.ok) {
            fetchPublishData();
            setCheckedList([]);
            isLoading(false);
            const response = await res.json();
            if (response.length === 0) {
              toast.success(
                <NotifySuccess header={"Success"}>
                  <Typography variant="body2">
                    Files Rejected Successfully
                  </Typography>
                </NotifySuccess>,
                {
                  icon: false,
                  closeButton: false,
                }
              );
            } else {
              let errormessage = "";
              response.forEach((RequestId: number) => {
                errormessage = errormessage + RequestId + ", ";
              });
              toast.error(
                <NotifyError header={"Error"}>
                  <Typography variant="body2">
                    Failed to Reject RequestId's -
                    {errormessage.substring(0, errormessage.length - 2)}
                  </Typography>
                </NotifyError>,
                {
                  icon: false,
                  closeButton: false,
                }
              );
            }
          } else if (res.status === 408) {
            isLoading(false);
            setCheckedList([]);
            toast.error(
              <NotifyError header={"Error"}>
                <Typography variant="body2">
                  {TOAST_MESSAGE.FAILED_TO_CONNECT}
                </Typography>
              </NotifyError>,
              {
                icon: false,
                closeButton: false,
              }
            );
          } else {
            setCheckedList([]);
            isLoading(false);
            toast.error(
              <NotifyError header={"Error"}>
                <Typography variant="body2">
                  {TOAST_MESSAGE.INTERNAL_SERVER_ERROR}
                </Typography>
              </NotifyError>,
              {
                icon: false,
                closeButton: false,
              }
            );
          }
        });
      }
    }
    setCommentPopup(false);
    setReviewComment("");
    setReviewStatus("");
    setPublishDataDto(null);
  };

  const handleCommentCancel = () => {
    setCommentPopup(false);
    setReviewComment("");
    setReviewStatus("");
    setPublishDataDto(null);
  };

  const handleViewLink = (featureName: string) => {
    const findFeature =
      reviewFeatures &&
      reviewFeatures.find((feature: Feature) => {
        return feature.name === featureName.replaceAll("_", " ");
      });
    if (findFeature) {
      setFeatureData(findFeature);
    } else {
      setNotFoundPopup(true);
    }
  };

  const handleViewBack = () => {
    setFeatureData(null);
  };

  const handleNotFoundCancel = () => {
    setNotFoundPopup(false);
  };
  const lastIndex = currentPage * itemsPerPage;
  const firstIndex = lastIndex - itemsPerPage;
  const currentPageItems = selectedUserDetails?.slice(firstIndex, lastIndex);

  if (reviewFeatures === null) {
    return (
      <Box className="loaderIconStylings">
        <CircularProgress disableShrink />
      </Box>
    );
  }

  return (
    <Box>
      {isDisable && <Box className={publishstyles.overlay}></Box>}
      {isDisable && (
        <Box className={publishstyles.loaderContainer}>
          <CircularProgress disableShrink />
        </Box>
      )}
      {!featureData && (
        <>
          <Box sx={{ marginBottom: "5rem" }}>
            <FormControl sx={{ width: "18%", margin: "1rem" }}>
              <InputLabel>Username</InputLabel>
              <Select
                size="small"
                value={selectedUser}
                label="userList"
                name="userList"
                onChange={handleUserSelect}
                defaultValue="ALL"
              >
                {Array.from(userNameList).map((username) => (
                  <MenuItem
                    key={username}
                    value={username}
                  >
                    <ListItemText primary={username} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Container className={reviewStylings.mainContainer}>
              <Box height="64vh">
                <Typography
                  variant="h5"
                  sx={{ padding: "2%" }}
                >
                  Publish Requests
                </Typography>
                <TableContainer
                  sx={{ maxHeight: "52vh", position: "sticky" }}
                  className="tableBody"
                >
                  <Table
                    stickyHeader
                    aria-label="sticky table"
                  >
                    <TableHead className="table-row-odd">
                      <TableRow>
                        <TableCell className="tableHeader">
                          <Checkbox
                            checked={
                              currentPageItems.length !== 0 &&
                              currentPageItems.every((item) =>
                                checkedList.includes(item)
                              )
                            }
                            disabled={currentPageItems.length === 0}
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell className="tableHeader">
                          Request Id
                        </TableCell>
                        <TableCell className="tableHeader">File Name</TableCell>
                        <TableCell className="tableHeader">
                          Submitted By
                        </TableCell>
                        <TableCell className="tableHeader">
                          Submitted Date
                        </TableCell>
                        <TableCell className="tableHeader">Comment</TableCell>
                        <TableCell className="tableHeader"> Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentPageItems.length > 0 ? (
                        <>
                          {currentPageItems.map((userDetails, userIndex) => (
                            <TableRow key={userIndex}>
                              <TableCell>
                                <Checkbox
                                  checked={checkedList.includes(userDetails)}
                                  onChange={() => handleSelect(userIndex)}
                                />
                              </TableCell>
                              <TableCell>{userDetails.publishId}</TableCell>
                              <TableCell>
                                <Link
                                  sx={{
                                    color: "#fe524a",
                                    textDecoration: "underline",
                                  }}
                                  onClick={() =>
                                    handleViewLink(userDetails.featureName)
                                  }
                                >
                                  {userDetails.featureName}
                                </Link>
                              </TableCell>
                              <TableCell>{userDetails.createdBy}</TableCell>
                              <TableCell>
                                {dayjs(userDetails.createdTimeStamp).format(
                                  "YYYY-MM-DD HH:mm:ss"
                                )}
                              </TableCell>
                              <TableCell
                                style={{
                                  maxWidth: "200px",
                                  overflow: "hidden",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {userDetails.publishComment || " - "}
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="large"
                                  disableRipple={true}
                                  disabled={
                                    !user.permissionsList.includes(
                                      "ACCEPT/REJECT FEATURE"
                                    ) || checkedList.length !== 0
                                  }
                                  onClick={() =>
                                    handleRequestReject(userDetails)
                                  }
                                >
                                  <HighlightOffIcon />
                                </IconButton>
                                <IconButton
                                  size="large"
                                  disableRipple={true}
                                  disabled={
                                    !user.permissionsList.includes(
                                      "ACCEPT/REJECT FEATURE"
                                    ) || checkedList.length !== 0
                                  }
                                  onClick={() =>
                                    handleRequestApprove(userDetails)
                                  }
                                >
                                  <CheckCircleOutlineIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      ) : (
                        <TableRow>
                          <TableCell
                            align="center"
                            height="300vh"
                            colSpan={7}
                          >
                            <Typography variant="body2">No Requests</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Container>
          </Box>
          <Box className={reviewStylings.actionBox}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {checkedList.length > 0 && (
                <Typography
                  sx={{
                    margin: "6px 1rem 0rem 0rem",
                    color: "#191F1C",
                  }}
                >
                  Selected : ({checkedList.length})
                </Typography>
              )}
              {/* pagination */}
              <Pagination
                rows={String(itemsPerPage)}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalDataLength={selectedUserDetails.length}
                dataPerPage={itemsPerPage}
                handleItemsPerPageChange={(e) => handleItemsPerPageChange(e)}
                fieldName="Requests"
              />
            </Box>
            {/* action buttons */}
            <Box className={reviewStylings.actionButtonsBox}>
              <Button
                variant="outlined"
                disabled={
                  !user.permissionsList.includes("ACCEPT/REJECT FEATURE") ||
                  checkedList.length === 0
                }
                onClick={() => handleRequestReject()}
              >
                Reject All
              </Button>
              <Button
                variant="outlined"
                disabled={
                  !user.permissionsList.includes("ACCEPT/REJECT FEATURE") ||
                  checkedList.length === 0
                }
                onClick={() => handleRequestApprove()}
              >
                Approve All
              </Button>
            </Box>
          </Box>
        </>
      )}
      {featureData && (
        <ViewFeature
          fileIndex={-1}
          featureData={featureData}
          stepDefList={[]}
          onBack={handleViewBack}
        ></ViewFeature>
      )}
      {commentPopup && (
        <WarningPopup
          isOpen={true}
          onClose={handleCommentCancel}
          actionButtons={[
            {
              name: "Confirm",
              onButtonClick: handleCommentOk,
              isDisabled: reviewComment === "",
            },
            {
              name: "Cancel",
              onButtonClick: handleCommentCancel,
            },
          ]}
        >
          <Typography sx={{ paddingBottom: "5%" }}>
            Please add comment message for Review.
          </Typography>
          <TextareaAutosize
            style={{ width: "102%", height: "90px" }}
            required
            id="PRcomment"
            maxRows={10}
            minRows={4}
            placeholder="Enter comment"
            value={reviewComment}
            onChange={(e) => setReviewComment(e.target.value)}
          />
        </WarningPopup>
      )}
      {notFoundPopup && (
        <WarningPopup
          isOpen={true}
          onClose={handleNotFoundCancel}
        >
          <Typography sx={{ paddingBottom: "5%", textAlign: "center" }}>
            The file you're trying to access is unavailable.
          </Typography>
        </WarningPopup>
      )}
    </Box>
  );
};

export default Review;
